"use client";
import React, { useState, useEffect, useCallback, forwardRef } from "react";
import { addDataLayer } from "@/helpers/dataLayer";
import { sendSalesforceInteraction } from "@/helpers/cdnSdk";
import useWindowSize from "@/utils/hooks/useWindowSize";
import banners from "@/utils/bannerData";
import { EmblaOptionsType, EmblaCarouselType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import "./embla.css";
import { widthSizes } from "@/utils/constant";
import { getUTMParameter } from "@/helpers/getUTMParameters";
import ImageWrapper from "../ImageWrapper";
import { Button } from "@dxp-feed/ui/Button";
import { Box } from "@dxp-feed/ui/Box";
import { Text } from "@dxp-feed/ui/Text";
import OfferCardBadge from "../Common/OfferCardBadge";
import { useAppSelector, useRef } from "@/utils/hooks/reactUseCustomHooks";
// Types
type EmblaSliderProps = {
  options?: EmblaOptionsType;
  align?: "start" | "center" | "end";
  images?: string[];
  slidesPerView?: {
    base?: number;
    xsm?: number;
    sm?: number;
    ssm?: number;
    md?: number;
    lg?: number;
    xl?: number;
    xml?: number;
    xxl?: number;
  };
  slidesToScroll?: {
    base?: number;
    xsm?: number;
    sm?: number;
    ssm?: number;
    md?: number;
    lg?: number;
    xl?: number;
    xml?: number;
    xxl?: number;
  };
  thumbnails?: boolean;
  showControls?: boolean;
  children?: React.ReactNode;
  className?: string;
  rowClassName?: string;
  viewportClassName?: string;
  containerClassName?: string;
  slideClassName?: string;
  slideImageClassName?: string;
  slideFullViewClassName?: string;
  containScrollSnap?: false | string;
  nextBtnClassName?: string;
  prevBtnClassName?: string;
  dotsClassName?: string;
  thumbnailsClassName?: string;
  emblaControlsClassName?: string;
  onClick?: () => void;
  carouselData?: any;
  bannerKey?: boolean;
  thumbIndex?: number;
  onExpandClick?: () => void;
  sendSelectedIndex?: (index: number) => void;
  promotionsItemsData?: any;
  disableActiveForFirstLast?: boolean;
  productName?:string
};

declare global {
  interface Window {
    firstBannerViewed?: boolean;
  }
}

// Main EmblaSlider Component
const EmblaSlider = forwardRef<HTMLDivElement, EmblaSliderProps>(
  (
    {
      options = { loop: true, dragFree: true }, // Default options
      align = "start",
      containScrollSnap = false,
      images,
      className = "",
      rowClassName = "",
      viewportClassName = "",
      containerClassName = "",
      slideClassName = "",
      slideImageClassName = "",
      slideFullViewClassName = "",
      nextBtnClassName = "",
      prevBtnClassName = "",
      dotsClassName = "",
      thumbnailsClassName = "",
      emblaControlsClassName = "",
      thumbnails = false,
      showControls = true,
      slidesPerView={
        base: 1.2,
        sm: 2.07,
        ssm: 2.8,
        md: 3.32,
        lg: 4,
        xl: 5,
        xxl: 6,
      },
      slidesToScroll={
        base: 1,
        sm: 2,
        ssm: 2,
        md: 3,
        lg: 4,
        xl: 5,
        xxl: 6,
      },
      thumbIndex,
      onExpandClick,
      onClick,
      sendSelectedIndex,
      children,
      carouselData,
      bannerKey = false,
      promotionsItemsData,
      disableActiveForFirstLast,
      productName
    },
    ref
  ) => {
    const [currentSlidesPerView, setCurrentSlidesPerView] = useState(
      slidesPerView?.base ?? 1
    );

    const [currentSlidesToScroll, setCurrentSlidesToScroll] = useState(
      slidesToScroll?.base ?? 1
    );
    const [isLoaded, setIsLoaded] = useState(false);
    const { loggedIn: userLoggedIn, uid: Uid, isLoading: isloading } = useAppSelector(
      (state) => state.gigyaslice
    );
    const viewedBannersRef = useRef<Set<number>>(new Set());

    const mergedOptions = {
      align,
      className,

      containInViewport: true,
      containScrollSnap,
      ...options,
      slidesToScroll: currentSlidesToScroll,
    };
    const [emblaRef, emblaApi] = useEmblaCarousel(mergedOptions);

    const cascadeValues = (breakpoints: { [key: string]: number }) => {
      const cascaded = { ...breakpoints };

      // Order matters: from smaller to larger
      const sizes = [
        "base",
        "xsm",
        "sm",
        "ssm",
        "md",
        "lg",
        "xl",
        "xml",
        "xxl",
      ];
      for (let i = 1; i < sizes.length; i++) {
        const prevSize = sizes[i - 1];
        const currentSize = sizes[i];

        if (cascaded[currentSize] === undefined) {
          cascaded[currentSize] = cascaded[prevSize];
        }
      }

      return cascaded;
    };
    // Cascade slidesPerView and slidesToScroll
    const cascadedSlidesPerView = cascadeValues(slidesPerView ?? {});
    const cascadedSlidesToScroll = cascadeValues(slidesToScroll ?? {});

    useEffect(() => {
      const updateResponsiveValues = () => {
        const width = window.innerWidth;

        if (width < 374) {
          setCurrentSlidesPerView(cascadedSlidesPerView.base);
          setCurrentSlidesToScroll(cascadedSlidesToScroll.base);
        } else if (width < 413) {
          setCurrentSlidesPerView(cascadedSlidesPerView.xsm);
          setCurrentSlidesToScroll(cascadedSlidesToScroll.xsm);
        } else if (width < 576) {
          setCurrentSlidesPerView(cascadedSlidesPerView.sm);
          setCurrentSlidesToScroll(cascadedSlidesToScroll.sm);
        } else if (width < 768) {
          setCurrentSlidesPerView(cascadedSlidesPerView.ssm);
          setCurrentSlidesToScroll(cascadedSlidesToScroll.ssm);
        } else if (width < 992) {
          setCurrentSlidesPerView(cascadedSlidesPerView.md);
          setCurrentSlidesToScroll(cascadedSlidesToScroll.md);
        } else if (width < 1100) {
          setCurrentSlidesPerView(cascadedSlidesPerView.lg);
          setCurrentSlidesToScroll(cascadedSlidesToScroll.lg);
        } else if (width < 1280) {
          setCurrentSlidesPerView(cascadedSlidesPerView.xl);
          setCurrentSlidesToScroll(cascadedSlidesToScroll.xl);
        } else if (width < 1401) {
          setCurrentSlidesPerView(cascadedSlidesPerView.xml);
          setCurrentSlidesToScroll(cascadedSlidesToScroll.xml);
        } else {
          setCurrentSlidesPerView(cascadedSlidesPerView.xxl);
          setCurrentSlidesToScroll(cascadedSlidesToScroll.xxl);
        }
      };

      updateResponsiveValues();
      window.addEventListener("resize", updateResponsiveValues);
      return () => window.removeEventListener("resize", updateResponsiveValues);
    }, [cascadedSlidesPerView, cascadedSlidesToScroll]);

    const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(
      emblaApi ?? undefined,
      sendSelectedIndex ?? (() => { }),
      disableActiveForFirstLast
    );
    const {
      prevBtnDisabled,
      nextBtnDisabled,
      onPrevButtonClick,
      onNextButtonClick,
    } = usePrevNextButtons(emblaApi ?? undefined);

    const slideStyle = {
      flex: `0 0 ${100 / currentSlidesPerView}%`,
    };
    const { width } = useWindowSize();
    const bannerData =
      width <= widthSizes.fullWidthBannerData
        ? banners.fullWidthMobileBannerData
        : banners.fullWidthBannerData;
        
    useEffect(() => {
      setTimeout(() => {
        setIsLoaded(true);
      }, 100);
      if (bannerKey) {
        try {
          if (!isloading) {
            if (!emblaApi || !carouselData || carouselData.length == 0) return;
            // Trigger first view event only once for first banner
            if (typeof window !== "undefined" && !window.firstBannerViewed) {
              const currentIndex = emblaApi.selectedScrollSnap();
              if (currentIndex === 0 && !viewedBannersRef?.current?.has(0)) {
                const currentBanner = carouselData[currentIndex];
                addDataLayer({
                  event: "banner_view",
                  click_text: currentBanner?.imagealt || "default",
                  click_url: `${currentIndex + 1}`,
                  page_title: document.title,
                });
                sendSalesforceInteraction({
                  name: "homepage_view",
                  banner: process.env.NEXT_PUBLIC_MERCHANT_IDENTIFIER,
                  isAnonymous: Uid ? 0 : 1,
                  gigyaUid: Uid,
                  text: currentBanner?.imagealt || "default",
                  section: "banner",
                  utmSource: getUTMParameter("utm_source"),
                  utmMedium: getUTMParameter("utm_medium"),
                  utmCampaign: getUTMParameter("utm_campaign"),
                });

                // Set window flag to prevent firing again in this session
                viewedBannersRef?.current?.add(0);
                window.firstBannerViewed = true;
              }
            }
            const onSelect = () => {
              const selectedIndex = emblaApi.selectedScrollSnap();

              // If the banner at the current index has not been viewed
              if (!viewedBannersRef.current?.has(selectedIndex)) {
                viewedBannersRef.current?.add(selectedIndex);

                const currentBanner = carouselData[selectedIndex];
                if (currentBanner) {
                  addDataLayer({
                    event: "banner_view",
                    click_text: currentBanner?.imagealt || "default",
                    click_url: `${selectedIndex + 1}`,
                    page_title: document.title,
                  });
                  sendSalesforceInteraction({
                    name: "homepage_view",
                    banner: process.env.NEXT_PUBLIC_MERCHANT_IDENTIFIER,
                    isAnonymous: Uid ? 0 : 1,
                    gigyaUid: Uid,
                    text: currentBanner?.imagealt ? currentBanner?.imagealt : "default",
                    section: "banner",
                    utmSource: getUTMParameter("utm_source"),
                    utmMedium: getUTMParameter("utm_medium"),
                    utmCampaign: getUTMParameter("utm_campaign"),
                  });
                }
              }
            };
            emblaApi.on("select", onSelect);
            return () => {
              emblaApi.off("select", onSelect);
            };
          }
        } catch (error) {
          console.error("Error in EmblaSlider:", error);
        }
      }
      // console.log("isLoaded isLoaded  isLoaded ==================>:", isLoaded);
    }, [emblaApi, carouselData, bannerKey, isLoaded, isloading]);

    useEffect(() => {
      if (emblaApi && ref) {
        // @ts-expect-error juset
        if (ref?.onScrollCapture) {
          // @ts-expect-error juset
          emblaApi.on("slidesInView", ref?.onScrollCapture);
        }
      }
    }, [emblaApi]);

    return (
      <section
        className={`embla__wrapper mx-auto w-full max-w-full ${className ?? ""}`}
      >
        {isLoaded && (
          <Box as="div" className={`embla__row ${rowClassName}`}>
            <Box
              as="div"
              className={`embla__viewport pt-[2px] !pb-2 relative ${viewportClassName}`}
              tabIndex={-1}
              ref={emblaRef}
            >
              <Box
                as="div"
                className={`embla__container ${containerClassName}`}
                tabIndex={-1}
                ref={ref}
              >
                {emblaApi && images && images.length > 0
                  ? images.map((src, index) => (
                    <Box
                      as="div"
                      className={`aspect-[8/7] embla__slide ${slideClassName} ${index === selectedIndex && !disableActiveForFirstLast ? "embla__slide--active" : ""}
                  items-stretch relative tab
                   ${index === selectedIndex && (disableActiveForFirstLast ? index !== 0 && index !== images.length - 1 : true) ? "embla__slide--active" : ""} ${slideFullViewClassName}`}
                      key={src}
                      style={slideStyle}
                      tabIndex={-1}
                    >
                      <ImageWrapper
                            src={src}
                            alt={`Slide ${index + 1}`}
                            fill
                            className={`embla__slide__img !object-contain ${slideImageClassName}`}
                            tabIndex={-1}
                            onClick={onClick}
                            sizes="(max-width: 768px) 100vw, 400px"
                          />
                      <Box className="OfferBageCon [&>div]:left-[20px] [&>div>img]:w-[65px] [&>div>img]:h-[65px]">
                        <OfferCardBadge promotions={promotionsItemsData} />
                      </Box>
                    </Box>
                  ))
                  : React.Children.map(children, (child, index) => (
                    <Box
                      key={index}
                      as="div"
                      className={`embla__slide items-stretch tab ${index === selectedIndex &&
                        (!disableActiveForFirstLast ||
                          (index !== 0 &&
                            index !== (images?.length || 0) - 1))
                        ? "embla__slide--active"
                        : ""
                        } ${slideClassName}`}
                      style={slideStyle}
                    >
                      {child}
                    </Box>
                  ))}
              </Box>
              {onExpandClick && (
                <Button
                  onClick={onExpandClick}
                  aria-label="Expand Icon"
                  variant="icon"
                  size="none"
                  className="absolute right-0 bottom-0 cursor-pointer"
                >
                  <ImageWrapper
                    src="/assets/products/expand-icon.svg"
                    alt={"expand icon"}
                    width={40}
                    height={40}
                  />
                </Button>
              )}
            </Box>

            {thumbnails &&
              images &&
              (images?.length > 0 ||
                (children &&
                  React.Children.count(children) > currentSlidesPerView)) && (
                <Thumbnails
                  thumbIndex={selectedIndex}
                  className={`flex ${emblaApi && images?.length <= 3
                    ? images?.length == 1
                      ? "justify-start gap-2"
                      : images?.length == 2
                        ? "justify-start gap-14"
                        : "justify-between gap-2"
                    : images?.length > 3
                      ? "justify-start gap-14"
                      : "justify-between gap-2"
                    } mt-4 ${thumbnailsClassName}`}
                >
                  {emblaApi && images?.length > 0
                    ? images?.map((src, index) => (
                      <Button
                        key={src}
                        variant="icon"
                        className={`embla__thumbnail p-0 rounded-2xl flex-shrink-0 ${index === selectedIndex
                          ? "embla__thumbnail--selected border border-solid border-black"
                          : ""
                          }`}
                        onClick={() => emblaApi?.scrollTo(index)}
                        aria-label={`Thumbnail ${index + 1}`}
                      >
                        <ImageWrapper
                          width={100}
                          height={100}
                          src={src}
                          alt={`${productName} ${index + 1}`}
                        />
                      </Button>
                    ))
                    : React.Children.map(children, (child, index) => (
                      <Button
                        key={index}
                        className={`embla__thumbnail_custom flex-shrink-0 ${index === selectedIndex
                          ? "embla__thumbnail--selected"
                          : ""
                          }`}
                        aria-label="scroll to button"
                        onClick={() => emblaApi?.scrollTo(index)}
                      >
                        {child}
                      </Button>
                    ))}
                </Thumbnails>
              )}

            {showControls &&
              ((images && images.length > currentSlidesPerView) ||
                (children &&
                  React.Children.count(children) > currentSlidesPerView)) && (
                <Box
                  as="div"
                  className={`embla__controls !flex  !justify-center lg:!mt-6 !mt-0 ${emblaControlsClassName}`}
                >
                  <Box as="div" className="embla__buttons !flex">
                    <PrevButton
                      onClick={onPrevButtonClick}
                      disabled={prevBtnDisabled}
                      prevBtnClassName={prevBtnClassName}
                      aria-label="Previous Button"
                    />
                    <Box as="div" className="embla__dots">
                      {scrollSnaps.map((_, index) => (
                        <DotButton
                          key={index}
                          aria-label={`slide ${index}`}
                          onClick={() => onDotButtonClick(index)}
                          className={`${dotsClassName} embla__dot bg-neutral100 border border-neutral100 p-0 text-xl w-4 h-4  ${index === selectedIndex ? " embla__dot--selected !bg-brandSecondary w-8 !border-brandSecondary" : ""}`}
                        />
                      ))}
                    </Box>
                    <NextButton
                      onClick={onNextButtonClick}
                      disabled={nextBtnDisabled}
                      nextBtnClassName={nextBtnClassName}
                      aria-label="Next Button"
                    />
                  </Box>
                </Box>
              )}
          </Box>
        )}
      </section>
    );
  }
);

// Hook: Prev/Next Button Handlers
export const usePrevNextButtons = (
  emblaApi: EmblaCarouselType | undefined
): {
  prevBtnDisabled: boolean;
  nextBtnDisabled: boolean;
  onPrevButtonClick: () => void;
  onNextButtonClick: () => void;
} => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const onPrevButtonClick = useCallback(
    () => emblaApi?.scrollPrev(),
    [emblaApi]
  );
  const onNextButtonClick = useCallback(
    () => emblaApi?.scrollNext(),
    [emblaApi]
  );

  const onSelect = useCallback(() => {
    if (emblaApi) {
      setPrevBtnDisabled(!emblaApi.canScrollPrev());
      setNextBtnDisabled(!emblaApi.canScrollNext());
    }
  }, [emblaApi, EmblaSlider]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("reInit", onSelect).on("select", onSelect);
  }, [emblaApi, onSelect]);

  return {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  };
};

// Hook: Dot Button Handlers
export const useDotButton = (
  emblaApi: EmblaCarouselType | undefined,
  sendSelectedIndex: (index: number) => void,
  disableActiveForFirstLast?: boolean
): {
  selectedIndex: number;
  scrollSnaps: number[];
  sendSelectedIndex: (index: number) => void;
  onDotButtonClick: (index: number) => void;
} => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onDotButtonClick = useCallback(
    (index: number) => emblaApi?.scrollTo(index),
    [emblaApi]
  );

  const onInit = useCallback(() => {
    if (emblaApi) setScrollSnaps(emblaApi.scrollSnapList());
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (emblaApi) {
      setSelectedIndex(
        emblaApi.selectedScrollSnap() + (disableActiveForFirstLast ? 1 : 0)
      );
      sendSelectedIndex(emblaApi.selectedScrollSnap());
    }
  }, [emblaApi, EmblaSlider]);

  useEffect(() => {
    if (!emblaApi) return;
    onInit();
    onSelect();
    emblaApi.on("reInit", onInit).on("select", onSelect);
  }, [emblaApi, onInit, onSelect]);

  return { selectedIndex, scrollSnaps, sendSelectedIndex, onDotButtonClick };
};

// Component: Prev Button
export const PrevButton: React.FC<React.ComponentPropsWithoutRef<"button"> & { prevBtnClassName?: string }> = ({
  children,
  prevBtnClassName,
  ...restProps
}) => (
  <Button
    variant="primary"
    alt="previous"
    className={`${prevBtnClassName} embla__button embla__button--prev !border rounded-full border-brandBlack hover:border-brandBlack focus:border-brandBlack [&:before]:hidden  hover:bg-transparent focus:bg-transparent
    bg-transparent cursor-pointer w-[40px] h-[40px] flex items-center justify-center text-[#333] [&[disabled]]:cursor-not-allowed [&[disabled]]:border-[#dcdcdc]`}
    type="button"
    aria-label="previous"
    {...restProps}
  >
    <Box
      as="span"
      className="text-brandBlack icon-prev-arrow !text-sm"
    ></Box>
    {children}
  </Button>
);

// Component: Next Button
export const NextButton: React.FC<React.ComponentPropsWithoutRef<"button"> & { nextBtnClassName?: string }> = ({
  nextBtnClassName,
  children,
  ...restProps
}) => (
  <Button
    variant="primary"
    alt="next"
    aria-label="next"
    className={`${nextBtnClassName} embla__button embla__button--next !border rounded-full border-brandBlack hover:border-brandBlack focus:border-brandBlack bg-transparent [&:before]:hidden  hover:bg-transparent focus:bg-transparent
    cursor-pointer w-[40px] h-[40px] flex items-center justify-center text-[#333] [&[disabled]]:cursor-not-allowed [&[disabled]]:border-[#dcdcdc]`}
    type="button"
    {...restProps}
  >
    <Box
      as="span"
      className="text-brandBlack icon-next-arrow !text-sm"
    ></Box>
    {children}
  </Button>
);

// Component: Dots Button
export const DotButton: React.FC<
  React.ComponentPropsWithoutRef<"button">
> = ({ children, ...restProps }) => (
  <Button type="button" variant="icon" aria-label="dot" className={`p-0`} {...restProps}>
    {children}
  </Button>
);

// Component: Embla thumbnails
export const Thumbnails: React.FC<{
  className?: string;
  children?: React.ReactNode;
  thumbIndex?: number;
  onThumbIndexChange?: (index: number) => void;
}> = ({ className, children, thumbIndex, onThumbIndexChange }) => {
  const [thumbsRef, thumbsApi] = useEmblaCarousel({
    containScroll: "keepSnaps",
    dragFree: true,
    slidesToScroll: 1,
  });
  const totalThumbs = React.Children.count(children);
  const needsScroll = totalThumbs > 3;
  useEffect(() => {
    if (!thumbsApi || thumbIndex === undefined) return;
    thumbsApi.scrollTo(thumbIndex);
  }, [thumbsApi, thumbIndex]);

  return (
    <Box as="div" className={`embla__thumbnails ${className}`}>
      {needsScroll ? (
        <Box as="div" className="embla__viewport" ref={thumbsRef}>
          <Box as="div" className="embla__container flex gap-5 pl-6">
            {children}
          </Box>
        </Box>
      ) : (
        children
      )}
    </Box>
  );
};
export default EmblaSlider;

export function OffersCarousalSkeleton({ className }: any) {
  return (
    <div
      className={`flex gap-6 w-full overflow-hidden ${className ? className : ""}`}
    >
      {[...Array(6)].map((_, i) => (
        <div
          key={i}
          className={`flex flex-col rounded-2xl overflow-hidden shadow animate-pulse bg-white w-full max-w-[220px] shrink-0`}
        >
          <div className="h-6 bg-gray-300 rounded-t-2xl"></div>
          <div className="h-4 bg-gray-300 rounded w-[40%] mt-3 mx-3"></div>

          <div className="relative p-4">
            <div className="pt-[56.25%] bg-gray-300"></div>
            <div className="absolute top-0 right-2 h-10 w-10 bg-gray-300 rounded-full"></div>
            <div className="absolute bottom-0 right-2 h-10 w-20 bg-gray-300 rounded-lg"></div>
          </div>

          <div className="flex flex-col gap-2 p-4 bg-[#f4f4f4] mt-3">
            <div className="h-4 bg-gray-300 rounded w-full"></div>
            <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            <div className="h-4 bg-gray-300 rounded w-5/6"></div>
          </div>

          <div className="flex justify-center items-center py-2 bg-gray-100 border-t border-gray-300">
            <div className="mx-6 h-5 w-full bg-gray-300 rounded-full"></div>
          </div>
        </div>
      ))}
    </div>
  );
}
